import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useErrors } from 'Components/Organisms/ErrorsProvider/index.jsx';
import storage from 'Utils/storage.js';
import {
    getInvoicesExportColumns,
    getInvoicesExportColumnGroups,
    INVOICES_DISABLED_COLUMNS,
} from 'Utils/invoicesExportColumns.js';
import exportImage from 'assets/export-done.svg';
import Button from 'Components/Atoms/Button/Button.jsx';
import Switch from 'Components/Atoms/Switch/Switch';
import Select from 'Components/Atoms/Select/Select';
import Tabs, { Tab } from 'Components/Molecules/Tabs/Tabs';
import ColumnsSelector from 'Components/Molecules/ColumnsSelector/ColumnsSelector';
import Tooltip from 'Components/Molecules/Tooltip/Tooltip';
import { exportInvoices, getExportInvoices } from 'Services/invoices.js';
import { useInvoicesFilters } from 'Pages/Invoices/InvoicesFiltersProvider.jsx';
import useInfiniteInvoices from 'Hooks/useInfiniteInvoices.js';

const EXPORT_FORMATS = [
    { value: 'xlsx', label: 'Excel (.xlsx)' },
    { value: 'csv', label: 'CSV' },
    { value: 'json', label: 'JSON' },
    { value: 'pdf', label: 'PDF' },
    { value: 'html', label: 'HTML' },
];

export default function InvoicesExportDrawer(props) {
    const { onClose } = props;
    const { searchFilters, params } = useInvoicesFilters();
    const { data } = useInfiniteInvoices(searchFilters);
    const count = data?.pages?.[0]?.count || 0;

    const { t } = useTranslation('invoices');

    const COLUMN_GROUPS = getInvoicesExportColumnGroups(t);
    const INITIAL_COLUMNS = getInvoicesExportColumns(t);

    const [columns, setColumns] = useState([]);
    const [markAccountingDelivered, setMarkAccountingDelivered] = useState(false);
    const [markBreakdownTaxes, setMarkBreakdownTaxes] = useState(false);
    const [markBreakdownTaxonomies, setMarkBreakdownTaxonomies] = useState(false);
    const { showUnknownError } = useErrors();
    const [loading, setLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [format, setFormat] = useState('xlsx');
    const [exportUrl, setExportUrl] = useState('');
    const [activeTab, setActiveTab] = useState('data');

    const activeColumnsCount = columns.filter((c) => c.active).length;

    const generateInitialColumns = () => {
        const storageColumns = storage.getItem('export_columns_invoices_11_02b');
        if (storageColumns) {
            const storedColumns = JSON.parse(storageColumns);
            const keys = Object.keys(storedColumns);

            const newColumns = INITIAL_COLUMNS.map((c) => {
                const active = INVOICES_DISABLED_COLUMNS.includes(c.id) ? c.active : (storedColumns[c.id] ?? c.active);
                return { ...c, active };
            }).sort((a, b) => keys.indexOf(a.id) - keys.indexOf(b.id));

            setColumns(newColumns);
        } else {
            setColumns(INITIAL_COLUMNS);
        }
    };

    const generateInitialFormat = () => {
        const storageFormat = storage.getItem('export_format_invoices_11_02b');
        const formatExists = EXPORT_FORMATS.find((f) => f.value === storageFormat);

        if (formatExists) {
            setFormat(storageFormat);
        } else {
            setFormat('xlsx');
        }
    };

    useEffect(() => {
        generateInitialColumns();
        generateInitialFormat();
    }, []);

    const isTaxColumnSelected = columns.some((c) => c.id === 'amount_tax' && c.active);

    const handleExportInvoices = async () => {
        if (loading) return;
        setLoading(true);

        const objectColumns = columns.reduce((acc, c) => ({ ...acc, [c.id]: c.active }), {});
        storage.setItem('export_columns_invoices_11_02b', JSON.stringify(objectColumns));
        storage.setItem('export_format_invoices_11_02b', format);

        try {
            const exportColumns = columns.filter((c) => c.active).map((c) => c.id);
            const exportParams = {
                ...params,
                mark_as_accounting_delivered: markAccountingDelivered,
                breakdown_taxes: isTaxColumnSelected && markBreakdownTaxes,
                breakdown_taxonomies: markBreakdownTaxonomies,
                export_format: format,
                export_columns: exportColumns,
            };

            await exportInvoices(exportParams);
            setLoading(false);
        } catch (error) {
            if (error.response?.data?.description === 'requires_processing') {
                setTimeout(() => exportInvoicesCheckStatus(error.response.data.info.token), 3000);
            } else {
                reset();
                showUnknownError(error);
            }
        }
    };

    const exportInvoicesCheckStatus = async (exportId) => {
        getExportInvoices(exportId).then((body) => {
            if (!body) {
                reset();
                return;
            }

            if (body.url) {
                setExportUrl(body.url);
                setLoading(false);
                setShowSuccess(true);
            } else {
                setTimeout(() => exportInvoicesCheckStatus(exportId), 3000);
            }
        });
    };

    const reset = () => {
        setShowSuccess(false);
        setExportUrl('');
        setLoading(false);
        setActiveTab('data');
        setMarkAccountingDelivered(false);
        setMarkBreakdownTaxes(false);
        setMarkBreakdownTaxonomies(false);
        generateInitialColumns();
        generateInitialFormat();
        onClose();
    };

    const formatSelected = EXPORT_FORMATS.find((f) => f.value === format)?.label || '';

    if (showSuccess) {
        return (
            <div className="mx-auto mt-20 max-w-sm text-center">
                <div className="flex justify-center mb-6">
                    <img src={exportImage} width={75} height={74} alt="" />
                </div>

                <div className="mb-6">
                    <h4 className="mt-6">{t('export-success')}</h4>
                    <div className="mt-2 text-gray-500">
                        {t('export-success-description', { format: formatSelected })}
                    </div>
                </div>

                <Button variant="primary" href={exportUrl} target="_blank" rel="noreferrer" download>
                    {t('download')} {formatSelected}
                </Button>

                <div className="text-xs mt-4 text-gray-500">{t('link-expiration')}</div>
            </div>
        );
    }

    return (
        <div className="grid gap-4 grid-rows-[auto_1fr_auto] absolute inset-0">
            <div>
                <div className="mb-4">
                    <div className="text-sm text-gray-500">{t('export-description', { count })}</div>
                </div>

                <Tabs value={activeTab} onChange={setActiveTab}>
                    <Tab value="data">{t('data')}</Tab>
                    <Tab value="columns">
                        {t('columns')} ({activeColumnsCount}/{columns.length})
                    </Tab>
                </Tabs>
            </div>

            {activeTab === 'data' && (
                <div className="py-3">
                    <Select
                        options={EXPORT_FORMATS}
                        value={format}
                        label={t('format')}
                        onChange={(e) => setFormat(e)}
                        className="w-full mb-6"
                    />

                    <div className="grid gap-4">
                        <div className="flex gap-3 items-center">
                            <Switch
                                checked={markAccountingDelivered}
                                onCheckedChange={(ev) => setMarkAccountingDelivered(ev)}
                                id="mark-accounting-delivered"
                            />
                            <label htmlFor="mark-accounting-delivered" className="grid gap-0.5 cursor-pointer">
                                <span className="text-sm font-medium">{t('mark-as-accounting-delivered')}</span>
                                <span className="text-xs text-gray-500">
                                    {t('mark-as-accounting-delivered-description')}
                                </span>
                            </label>
                        </div>

                        {!isTaxColumnSelected && (
                            <div className="flex gap-3 items-center">
                                <Tooltip label={t('you-must-include-taxes-to-use-this-option')}>
                                    <span className="flex">
                                        <Switch id="mark-breakdown-taxes" disabled />
                                    </span>
                                </Tooltip>
                                <label htmlFor="mark-breakdown-taxes" className="grid gap-0.5 opacity-50">
                                    <div className="text-sm font-medium">{t('mark-breakdown-taxes')}</div>
                                    <div className="text-xs text-gray-500">{t('mark-breakdown-taxes-description')}</div>
                                </label>
                            </div>
                        )}

                        <div className="flex gap-3 items-center">
                            <Switch
                                checked={markBreakdownTaxonomies}
                                onCheckedChange={(ev) => setMarkBreakdownTaxonomies(ev)}
                                id="mark-breakdown-taxonomies"
                            />
                            <label htmlFor="mark-breakdown-taxonomies" className="grid gap-0.5 cursor-pointer">
                                <div className="text-sm font-medium">{t('mark-breakdown-taxonomies')}</div>
                                <div className="text-xs text-gray-500">
                                    {t('mark-breakdown-taxonomies-description')}
                                </div>
                            </label>
                        </div>

                        {isTaxColumnSelected && (
                            <div className="flex gap-3 items-center">
                                <Switch
                                    checked={markBreakdownTaxes}
                                    onCheckedChange={(ev) => setMarkBreakdownTaxes(ev)}
                                    id="mark-breakdown-taxes"
                                />
                                <label htmlFor="mark-breakdown-taxes" className="grid gap-0.5 cursor-pointer">
                                    <div className="text-sm font-medium">{t('mark-breakdown-taxes')}</div>
                                    <div className="text-xs text-gray-500">{t('mark-breakdown-taxes-description')}</div>
                                </label>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'columns' && (
                <ColumnsSelector columns={columns} onColumnsChange={setColumns} groups={COLUMN_GROUPS} />
            )}

            <div className="sticky bottom-0 bg-white pb-4 z-10">
                <div className="flex gap-2">
                    <Button variant="primary" isBusy={loading} onClick={handleExportInvoices} className="grow">
                        {t('export')}
                    </Button>
                    <Button variant="default" className="grow" onClick={reset}>
                        {t('cancel')}
                    </Button>
                </div>
            </div>
        </div>
    );
}

InvoicesExportDrawer.propTypes = {
    onClose: PropTypes.func.isRequired,
};
